import React, { useEffect, useState } from 'react';
import { CATEGORIES, NUM_OF_QUESTIONS, DIFFICULTY, QUESTIONS_TYPE } from '../../constants';
import { BookOpen, Brain, Award, Clock, List, Loader2, Plus, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';

const QuizForm = ({
  mockTest,
  setMockTest,
  mockTestOptions,
  activeMockTest,
  selectedCategories,
  setSelectedCategories,
  numOfQuestions,
  setNumOfQuestions,
  difficulty,
  setDifficulty,
  questionsType,
  setQuestionsType,
  countdownTime,
  handleTimeChange,
  fetchData,
  processing,
  allFieldsSelected,
  isDark = false,
}) => {
  const [animateTransition, setAnimateTransition] = useState(false);

  // Ensure there's at least one dropdown available on mount.
  useEffect(() => {
    if (selectedCategories.length === 0 && !mockTest) {
      setSelectedCategories(['']);
    }
  }, [selectedCategories, setSelectedCategories, mockTest]);

  // Add a new empty category dropdown.
  const addCategoryField = () => {
    setSelectedCategories([...selectedCategories, '']);
  };

  // Remove a category dropdown at a given index.
  const removeCategoryField = (index) => {
    const updatedCategories = selectedCategories.filter((_, i) => i !== index);
    setSelectedCategories(updatedCategories);
  };

  // Handle change in a specific category dropdown.
  const handleCategoryFieldChange = (index, value) => {
    const updatedCategories = selectedCategories.map((cat, i) =>
      i === index ? value : cat
    );
    setSelectedCategories(updatedCategories);
  };

  // Handle mock test selection with animation
  const handleMockTestChange = (e) => {
    const value = e.target.value;
    setAnimateTransition(true);
    setTimeout(() => {
      setMockTest(value);
      // Clear categories when mock test is selected
      if (value) {
        setSelectedCategories([]);
      } else {
        setSelectedCategories(['']);
      }
      setAnimateTransition(false);
    }, 300);
  };

  // Calculate questions per category (ignoring empty selections).
  const validCategories = selectedCategories.filter((cat) => cat);
  const questionsPerCategory =
    validCategories.length > 0
      ? Math.floor(numOfQuestions / validCategories.length)
      : numOfQuestions;

  // Theme-based classes
  const cardClass = isDark
    ? 'bg-dark-secondary/50 border border-white/5'
    : 'bg-white border border-gray-200 shadow-lg';

  const sectionClass = isDark
    ? 'bg-dark-tertiary/50 border-l-4 border-purple-500'
    : 'bg-purple-50 border-l-4 border-purple-500';

  const labelClass = isDark
    ? 'text-gray-200'
    : 'text-gray-800';

  const iconClass = isDark
    ? 'text-purple-400'
    : 'text-purple-700';

  const selectClass = isDark
    ? 'bg-dark-tertiary/50 border-white/10 text-white focus:border-purple-500/50'
    : 'bg-white border-gray-200 text-gray-900 focus:ring-purple-500';

  const inputClass = isDark
    ? 'bg-dark-tertiary/50 border-white/10 text-white placeholder-gray-500 focus:border-purple-500/50'
    : 'bg-white border-gray-200 text-gray-900 focus:ring-purple-500';

  return (
    <motion.div
      className={`max-w-4xl mx-auto p-6 rounded-2xl ${cardClass}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className={`space-y-6 transition-opacity duration-300 ${animateTransition ? 'opacity-50' : 'opacity-100'}`}>

        {/* Mock Test Selection */}
        <div className={`p-5 rounded-xl transition-all hover:shadow-md ${sectionClass}`}>
          <label className={`flex items-center gap-2 text-lg font-semibold mb-3 ${labelClass}`}>
            <BookOpen size={20} className={iconClass} />
            Choose a Mock Test (optional):
          </label>
          {mockTestOptions && mockTestOptions.length > 0 ? (
            <select
              value={mockTest}
              onChange={handleMockTestChange}
              className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${selectClass}`}
            >
              <option value="">Custom Quiz (Select Categories)</option>
              {mockTestOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          ) : (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              No mock tests available yet. Create one from the admin panel to enable this option.
            </p>
          )}
        </div>

        {/* Display Mock Test Details when selected */}
        {mockTest && (
          <motion.div
            className={`p-5 rounded-xl ${isDark
              ? 'bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/20'
              : 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200'
              }`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
          >
            <div className={`flex items-center gap-2 text-lg font-semibold mb-3 ${labelClass}`}>
              <BookOpen size={20} className={iconClass} />
              <span>{activeMockTest?.name || 'Mock Test'} Details</span>
            </div>
            {activeMockTest ? (
              <div className={`space-y-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {activeMockTest.description && (
                  <p>{activeMockTest.description}</p>
                )}
                <div className="flex flex-wrap gap-3">
                  {activeMockTest.questionCount && (
                    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-50 border border-purple-300 text-purple-900'
                      }`}>
                      {activeMockTest.questionCount} questions
                    </span>
                  )}
                  {activeMockTest.timeLimitSeconds && (
                    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1 ${isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-50 border border-purple-300 text-purple-900'
                      }`}>
                      <Clock size={14} />
                      {(() => {
                        const hours = Math.floor(activeMockTest.timeLimitSeconds / 3600);
                        const minutes = Math.floor((activeMockTest.timeLimitSeconds % 3600) / 60);
                        const parts = [];
                        if (hours) parts.push(`${hours}h`);
                        if (minutes || !hours) parts.push(`${minutes}m`);
                        return parts.join(' ');
                      })()}
                    </span>
                  )}
                </div>
                {activeMockTest.subjects ? (
                  <div className={`space-y-2 divide-y ${isDark ? 'divide-white/10' : 'divide-purple-100'}`}>
                    {activeMockTest.subjects.map((subject, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2">
                        <span>{subject.category || subject.name}</span>
                        {subject.percentage && (
                          <div className="flex items-center gap-2">
                            <div className={`w-32 h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'
                              }`}>
                              <div
                                className="h-full bg-gradient-to-r from-purple-600 to-pink-600 rounded-full"
                                style={{ width: `${subject.percentage}%` }}
                              ></div>
                            </div>
                            <span className={`font-medium ${isDark ? 'text-purple-400' : 'text-purple-900'}`}>
                              {subject.percentage}%
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                    This mock test pulls questions directly from its saved MCQ set.
                  </p>
                )}
              </div>
            ) : (
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Loading mock test summary...
              </p>
            )}
          </motion.div>
        )}

        {/* Dynamic Category Dropdowns - Only show when no mock test is selected */}
        {!mockTest && (
          <div className={`p-5 rounded-xl transition-all hover:shadow-md ${sectionClass}`}>
            <label className={`flex items-center gap-2 text-lg font-semibold mb-3 ${labelClass}`}>
              <List size={20} className={iconClass} />
              Select Quiz Categories:
            </label>
            <div className="space-y-3">
              {selectedCategories.map((category, index) => (
                <motion.div
                  key={index}
                  className={`flex items-center gap-2 p-3 rounded-xl transition-all ${isDark ? 'bg-dark-primary/50 border border-white/5' : 'bg-white shadow-sm border border-purple-100'
                    }`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <select
                    value={category}
                    onChange={(e) => handleCategoryFieldChange(index, e.target.value)}
                    className={`flex-1 p-3 border rounded-xl focus:outline-none focus:ring-2 ${selectClass}`}
                  >
                    <option value="">Select Category</option>
                    {CATEGORIES.map((option) => (
                      <option key={option.key} value={option.text}>
                        {option.text}
                      </option>
                    ))}
                  </select>
                  {selectedCategories.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCategoryField(index)}
                      className={`p-2 rounded-lg transition-colors ${isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'
                        }`}
                      aria-label="Remove category"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </motion.div>
              ))}

              <button
                type="button"
                onClick={addCategoryField}
                className={`flex items-center gap-2 w-full justify-center p-3 rounded-xl border border-dashed transition-colors ${isDark
                  ? 'text-purple-400 border-purple-500/30 hover:border-purple-500/50 hover:bg-purple-500/10'
                  : 'text-purple-600 border-purple-300 hover:border-purple-500 hover:bg-purple-50'
                  }`}
              >
                <Plus size={18} />
                Add Category
              </button>

              {validCategories.length > 0 && (
                <div className={`text-sm p-3 rounded-lg font-medium ${isDark ? 'bg-purple-500/10 text-purple-300' : 'bg-purple-100 text-purple-800'
                  }`}>
                  Questions per category: {questionsPerCategory}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Number of Questions */}
          <div className={`p-5 rounded-xl transition-all hover:shadow-md ${sectionClass}`}>
            <label className={`flex items-center gap-2 text-lg font-semibold mb-3 ${labelClass}`}>
              <Brain size={20} className={iconClass} />
              Number of Questions:
            </label>
            <select
              value={numOfQuestions}
              onChange={(e) => setNumOfQuestions(Number(e.target.value))}
              className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 ${selectClass}`}
            >
              {NUM_OF_QUESTIONS.map((option) => (
                <option key={option.key} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
          </div>

          {/* Difficulty Level */}
          <div className={`p-5 rounded-xl transition-all hover:shadow-md ${sectionClass}`}>
            <label className={`flex items-center gap-2 text-lg font-semibold mb-3 ${labelClass}`}>
              <Award size={20} className={iconClass} />
              Difficulty Level:
            </label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 ${selectClass}`}
            >
              {DIFFICULTY.map((option) => (
                <option key={option.key} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
          </div>

          {/* Questions Type */}
          <div className={`p-5 rounded-xl transition-all hover:shadow-md ${sectionClass}`}>
            <label className={`flex items-center gap-2 text-lg font-semibold mb-3 ${labelClass}`}>
              <List size={20} className={iconClass} />
              Questions Type:
            </label>
            <select
              value={questionsType}
              onChange={(e) => setQuestionsType(e.target.value)}
              className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 ${selectClass}`}
            >
              {QUESTIONS_TYPE.map((option) => (
                <option key={option.key} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
          </div>

          {/* Countdown Time */}
          <div className={`p-5 rounded-xl transition-all hover:shadow-md ${sectionClass}`}>
            <label className={`flex items-center gap-2 text-lg font-semibold mb-3 ${labelClass}`}>
              <Clock size={20} className={iconClass} />
              Time Limit:
            </label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <input
                  type="number"
                  name="hours"
                  min="0"
                  value={countdownTime.hours}
                  onChange={handleTimeChange}
                  placeholder="0"
                  className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
                  aria-label="Hours"
                />
                <div className={`text-xs text-center mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Hours</div>
              </div>
              <div>
                <input
                  type="number"
                  name="minutes"
                  min="0"
                  max="59"
                  value={countdownTime.minutes}
                  onChange={handleTimeChange}
                  placeholder="0"
                  className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
                  aria-label="Minutes"
                />
                <div className={`text-xs text-center mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Minutes</div>
              </div>
              <div>
                <input
                  type="number"
                  name="seconds"
                  min="0"
                  max="59"
                  value={countdownTime.seconds}
                  onChange={handleTimeChange}
                  placeholder="0"
                  className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
                  aria-label="Seconds"
                />
                <div className={`text-xs text-center mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Seconds</div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <motion.button
            onClick={fetchData}
            disabled={!allFieldsSelected || processing}
            className={`w-full py-4 px-6 rounded-xl text-white font-semibold text-lg flex items-center justify-center gap-2 transition-all ${!allFieldsSelected || processing
              ? isDark ? 'bg-gray-700 cursor-not-allowed text-gray-500' : 'bg-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:shadow-lg hover:shadow-purple-500/25'
              }`}
            whileHover={allFieldsSelected && !processing ? { scale: 1.02 } : {}}
            whileTap={allFieldsSelected && !processing ? { scale: 0.98 } : {}}
          >
            {processing ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Preparing Quiz...
              </>
            ) : (
              'Start Quiz'
            )}
          </motion.button>

          {!allFieldsSelected && (
            <p className={`text-sm mt-3 text-center ${isDark ? 'text-red-400' : 'text-red-500'}`}>
              Please complete all required fields to start the quiz
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default QuizForm;